import { importProvidersFrom, inject } from '@angular/core';
import { type CanActivateFn, type Route, type Routes, type UrlMatchResult, type UrlSegment, Router } from '@angular/router';
import { APP_PATHS } from '@dev-fast/types';
import { NgxsModule } from '@ngxs/store';

import { IsAuthGuard, IsAuthNotGuard } from '@app/auth';
import {
  type AppSurfaceNavigationItem,
  EnvironmentService,
  getEnvironmentAppSurface,
  getEnvironmentCaseItemRouteSegment,
  getEnvironmentGameRouteKey,
} from '@app/core/environment-service';
import {
  AutoGameActivateChildGuard,
  AutoGameDeactivateGuard,
  BanActivateGuard,
  bettingSurfaceGuard,
  casesOnlyGuard,
  geoBlockGuard,
  IsGameActiveGuard,
  OnlyDevGuard,
  OnlyServerGuard,
  staticFaqPageCanMatchGuard,
} from '@app/core/guards';
import { LanguageService } from '@app/core/language-service';
import { SeoType } from '@app/core/seo-service'; // eslint-disable-next-line @nx/enforce-module-boundaries
import { CaseBattleState } from '@app/games/lib/case-battle-v2'; // eslint-disable-next-line @nx/enforce-module-boundaries
import { MotivatorState } from '@app/games/lib/state/motivator';
import { LobbyService } from '@app/modules/lobby/src/lib/services/lobby-service';
import { LobbyState } from '@app/modules/lobby/src/lib/state';
import { getStaticUrl } from '@app/shared/utils/static-url';

import { bbConfig, environment } from '../environments/environment';
import { getAppLobbySeoPages } from './app-seo-data.util';
import { surfaceAuthWallCanActivateGuard, surfaceAuthWallCanMatchGuard } from './guards/surface-auth-wall.guard';

const lobbySeoPages = getAppLobbySeoPages(environment);
const lobbySeoKey = environment.LOBBY_SEO_KEY ?? 'LOBBY';
const appSurface = getEnvironmentAppSurface(environment);
const homeConfig = appSurface.home;
const raffleBgImagePreloads = [
  { id: 'raffle-bg-preload-3840w', href: getStaticUrl('/raffle/3840w.webp'), media: '(min-width: 3200px)' },
  {
    id: 'raffle-bg-preload-2560w',
    href: getStaticUrl('/raffle/2560w.webp'),
    media: '(min-width: 2160px) and (max-width: 3199.98px)',
  },
  {
    id: 'raffle-bg-preload-1920w',
    href: getStaticUrl('/raffle/1920w.webp'),
    media: '(min-width: 1440px) and (max-width: 2159.98px)',
  },
  {
    id: 'raffle-bg-preload-1280w',
    href: getStaticUrl('/raffle/1280w.webp'),
    media: '(min-width: 1280px) and (max-width: 1439.98px)',
  },
  {
    id: 'raffle-bg-preload-1024w',
    href: getStaticUrl('/raffle/1024w.webp'),
    media: '(min-width: 768px) and (max-width: 1279.98px)',
  },
  {
    id: 'raffle-bg-preload-600w',
    href: getStaticUrl('/raffle/600w.webp'),
    media: '(min-width: 600px) and (max-width: 767.98px)',
  },
  {
    id: 'raffle-bg-preload-430w',
    href: getStaticUrl('/raffle/430w.webp'),
    media: '(min-width: 430px) and (max-width: 599.98px)',
  },
  { id: 'raffle-bg-preload-320w', href: getStaticUrl('/raffle/320w.webp'), media: '(max-width: 429.98px)' },
] as const;
const x50RouteKey = getEnvironmentGameRouteKey(environment, APP_PATHS.games.x50.base);
const x50GamePath = `game/${x50RouteKey}`;
const casesRouteKey = getEnvironmentGameRouteKey(environment, APP_PATHS.games.cases.base);
const caseItemRouteSegment = getEnvironmentCaseItemRouteSegment(environment);
const upgraderRouteKey = getEnvironmentGameRouteKey(environment, APP_PATHS.games.upgrader.base);
const surfaceHomeMatcher = (segments: UrlSegment[]): UrlMatchResult | null => (segments.length === 0 ? { consumed: [] } : null);
const getPromoCaseRedirectTo = (caseId: number): string => `/game/${casesRouteKey}/${caseItemRouteSegment}/${caseId}`;
const loadNewLobbyComponent = (): ReturnType<NonNullable<Route['loadComponent']>> =>
  import('@app/modules/new-lobby').then(({ NewLobbyComponent }) => NewLobbyComponent);
const getNewLobbyProviders = (): NonNullable<Route['providers']> => [
  LobbyService,
  importProvidersFrom(NgxsModule.forFeature([LobbyState, CaseBattleState])),
];
const createNewLobbyRoute = (route: Pick<Route, 'path' | 'pathMatch' | 'canActivate'> & { data: NonNullable<Route['data']> }): Route => ({
  ...route,
  loadComponent: loadNewLobbyComponent,
  data: { ...route.data, preloadImages: raffleBgImagePreloads },
  providers: getNewLobbyProviders(),
});
const surfaceNavigationItemCanActivateGuard =
  (item: AppSurfaceNavigationItem): CanActivateFn =>
  () => {
    const environmentService = inject(EnvironmentService);

    if (environmentService.navigationItemIsEnabled(item)) {
      return true;
    }

    const router = inject(Router);
    const languageService = inject(LanguageService);

    return router.createUrlTree([languageService.getCurrentLangUrl('/')]);
  };
const fifaEventCanActivateGuard = surfaceNavigationItemCanActivateGuard('fifa');
const lobbyRoute = createNewLobbyRoute({
  path: '',
  pathMatch: 'full' as const,
  data: { seo: lobbySeoKey, seoType: [SeoType.TITLE, SeoType.MAIN] },
  canActivate: [geoBlockGuard],
});
const promoCaseRoutes: Route[] = [
  {
    path: 'hell-case',
    loadComponent: () => import('./pages/hell-case/hell-case.component').then((modules) => modules.HellCaseComponent),
    data: { seo: 'HELL', redirectTo: getPromoCaseRedirectTo(241) },
  },
  {
    path: 'key-drop',
    loadComponent: () => import('./pages/key-drop/key-drop.component').then((modules) => modules.KeyDropComponent),
    data: { seo: 'KEY', redirectTo: getPromoCaseRedirectTo(245) },
  },
  {
    path: 'skin-club',
    loadComponent: () => import('./pages/skin-club/skin-club.component').then((modules) => modules.SkinClubComponent),
    data: { seo: 'CLUB', redirectTo: getPromoCaseRedirectTo(242) },
  },
  {
    path: 'csgo-roll',
    loadComponent: () => import('./pages/csgo-roll/csgo-roll.component').then((modules) => modules.CsgoRollComponent),
    data: { seo: 'ROLL', redirectTo: getPromoCaseRedirectTo(244) },
  },
  {
    path: 'farm-skins',
    loadComponent: () => import('./pages/farm-skins/farm-skins.component').then((modules) => modules.FarmSkinsComponent),
    data: { seo: 'FARM', redirectTo: getPromoCaseRedirectTo(243) },
  },
].map((route) => ({
  ...route,
  canMatch: [surfaceAuthWallCanMatchGuard],
  canActivate: [IsAuthNotGuard],
  data: {
    ...route.data,
    seoType: [SeoType.NOTHING],
  },
}));
const casesHomeRoute: Route = {
  matcher: surfaceHomeMatcher,
  loadChildren: () => import('@app/games/lib/cases/cases-fast/cases.routes').then(({ routes: casesRoutes }) => casesRoutes),
  data: { seo: homeConfig?.seoKey ?? lobbySeoKey, seoType: [SeoType.TITLE, SeoType.MAIN] },
  providers: [importProvidersFrom(NgxsModule.forFeature([MotivatorState]))],
  canActivate: [geoBlockGuard],
};
const homeRoute = homeConfig?.kind === 'cases' ? casesHomeRoute : lobbyRoute;
const legacyLobbyRoute = homeConfig?.legacyLobbyPath
  ? [
      createNewLobbyRoute({
        path: homeConfig.legacyLobbyPath,
        data: { seo: lobbySeoKey, seoType: [SeoType.NOTHING] },
        canActivate: [surfaceAuthWallCanActivateGuard, geoBlockGuard],
      }),
    ]
  : [];

const getLegacyRoute = (): Route[] => {
  return bbConfig.legacyRoutes.map((route) => ({
    path: route,
    data: {
      seo: bbConfig.legacySeoKeys[route] ?? (route.startsWith('history/crash/') ? bbConfig.legacySeoKeys['history/crash'] : undefined),
    },
    loadComponent: () => import('@app/ui/components/plug/plug.component').then((m) => m.PlugComponent),
    canDeactivate: [AutoGameDeactivateGuard],
    canMatch: [surfaceAuthWallCanMatchGuard],
    canActivate: [BanActivateGuard],
    canActivateChild: [AutoGameActivateChildGuard, BanActivateGuard],
  }));
};

export const appRoutes: Routes = [
  ...lobbySeoPages.map((page) => ({
    path: page.routePath,
    loadComponent: () => import('@app/modules/lobby').then(({ LobbySeoPageComponent }) => LobbySeoPageComponent),
    data: {
      seo: page.countryName.toUpperCase(),
      seoType: [SeoType.TITLE],
      isLobbySeoPage: true,
      countryCode: page.countryCode,
      countryName: page.countryName,
      titleKey: page.titleKey,
      hreflang: page.hreflang,
      flagEmoji: page.flagEmoji,
      redirectToRootOnLangChange: true,
      setIndex: true,
    },
    providers: [LobbyService, importProvidersFrom(NgxsModule.forFeature([LobbyState]))],
    canActivate: [casesOnlyGuard, geoBlockGuard],
  })),
  homeRoute,
  ...legacyLobbyRoute,
  ...promoCaseRoutes,
  {
    path: APP_PATHS.login,
    data: { seo: 'LOGIN', seoType: [SeoType.H1, SeoType.TITLE, SeoType.DESCRIPTION] },
    loadComponent: () => import('login').then(({ LoginComponent }) => LoginComponent),
  },
  {
    path: APP_PATHS.seasons,
    loadChildren: () => import('@app/modules/seasons/routes').then(({ routes }) => routes),
    canMatch: [surfaceAuthWallCanMatchGuard],
    canDeactivate: [AutoGameDeactivateGuard],
    canActivate: [BanActivateGuard, geoBlockGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    // страница заглушка для пререндера неважных сео страниц
    path: 'noindex',
    canActivate: [OnlyServerGuard],
    canLoad: [OnlyServerGuard],
    loadComponent: () => import('@app/ui/components/noindex/noindex.component').then(({ NoIndexComponent }) => NoIndexComponent),
  },
  {
    path: 'kyc',
    loadComponent: () => import('@app/modules/rain').then(({ RainKYCComponent }) => RainKYCComponent),
    canActivate: [casesOnlyGuard, IsGameActiveGuard, BanActivateGuard, geoBlockGuard],
    data: { gameKey: 'rain' },
  },
  ...getLegacyRoute(),
  {
    path: 'history/fast/all',
    redirectTo: 'history/fast',
    pathMatch: 'full',
  },
  {
    path: 'balance/withdrawal',
    redirectTo: 'withdrawal/method',
    pathMatch: 'full',
  },
  {
    path: 'buy-coins',
    redirectTo: 'refill',
    pathMatch: 'full',
  },
  {
    path: 'cases',
    redirectTo: `game/${casesRouteKey}`,
    pathMatch: 'full',
  },
  {
    path: 'upgrader',
    redirectTo: `game/${upgraderRouteKey}`,
    pathMatch: 'full',
  },
  {
    path: 'hilo',
    redirectTo: 'game/hilo',
    pathMatch: 'full',
  },
  {
    path: 'classic',
    redirectTo: 'game/classic',
    pathMatch: 'full',
  },
  {
    path: 'double',
    redirectTo: 'game/double',
    pathMatch: 'full',
  },
  {
    path: 'x50',
    redirectTo: x50GamePath,
    pathMatch: 'full',
  },
  ...(x50RouteKey !== APP_PATHS.games.x50.base
    ? [
        {
          path: x50RouteKey,
          redirectTo: x50GamePath,
          pathMatch: 'full' as const,
        },
      ]
    : []),
  {
    path: 'slot',
    redirectTo: 'game/slot',
    pathMatch: 'full',
  },
  {
    path: 'personal',
    redirectTo: 'account',
    pathMatch: 'full',
  },
  {
    path: 'account/main',
    redirectTo: 'account',
    pathMatch: 'full',
  },
  {
    path: 'personal/balance',
    redirectTo: 'account/balance',
    pathMatch: 'full',
  },
  {
    path: 'market/inventory',
    redirectTo: 'inventory',
    pathMatch: 'full',
  },
  {
    path: 'faucet',
    redirectTo: 'game',
    pathMatch: 'full',
  },
  {
    path: 'bonuses',
    redirectTo: 'game',
    pathMatch: 'full',
  },
  {
    path: 'bonuses/quest',
    redirectTo: 'game',
    pathMatch: 'full',
  },
  {
    path: 'bonuses/faucet',
    redirectTo: 'game',
    pathMatch: 'full',
  },
  {
    path: 'bonuses/referral',
    redirectTo: 'account/referral',
    pathMatch: 'full',
  },
  {
    path: 'partnership',
    redirectTo: 'account/referral',
    pathMatch: 'full',
  },
  {
    path: 'bonuses/referral/:id',
    redirectTo: ({ params }) => {
      const router = inject(Router);
      const id: string | undefined = params['id'];

      return id ? router.createUrlTree(['account/referral/campaign'], { queryParams: { id } }) : 'account/referral/campaigns';
    },
  },
  {
    path: 'support',
    redirectTo: 'game',
    pathMatch: 'full',
  },
  {
    path: 'support/requests',
    redirectTo: 'game',
    pathMatch: 'full',
  },
  // NOTE: redirect form bb
  {
    path: 'event',
    redirectTo: 'events',
    pathMatch: 'full',
  },

  {
    path: 'withdrawal',
    redirectTo: '/(modal:transaction/withdrawal)',
    pathMatch: 'full',
  },
  {
    path: 'withdrawal/method',
    redirectTo: '/(modal:transaction/withdrawal)',
    pathMatch: 'full',
  },
  {
    path: 'withdrawal/common',
    redirectTo: '/(modal:transaction/withdrawal)',
    pathMatch: 'full',
  },
  {
    path: 'withdrawal/history',
    redirectTo: '/(modal:transaction/withdrawal/history)',
    pathMatch: 'full',
  },
  {
    path: 'refill/method',
    redirectTo: '/(modal:transaction/refill)',
    pathMatch: 'full',
  },
  {
    path: 'refill/prepaid',
    redirectTo: '/(modal:transaction/refill/giftcards)',
    pathMatch: 'full',
  },
  {
    path: 'refill/history',
    redirectTo: 'account/balance',
    pathMatch: 'full',
  },
  {
    path: 'refill',
    redirectTo: '/(modal:transaction/refill)',
    pathMatch: 'full',
  },
  {
    path: 'refill/skins',
    redirectTo: '/(modal:transaction/refill/skins)',
    pathMatch: 'full',
  },
  {
    path: APP_PATHS.games.offerings.base,
    loadChildren: () => import('gifts-fast').then(({ fastRoutes }) => fastRoutes),
    data: {
      seo: 'GIVEAWAYS',
      seoType: [SeoType.TITLE, SeoType.DESCRIPTION, SeoType.FAQ],
    },
    canDeactivate: [AutoGameDeactivateGuard],
    canMatch: [surfaceAuthWallCanMatchGuard],
    canActivate: [BanActivateGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    path: 'vip',
    loadComponent: () => import('@app/modules/vip').then(({ VipComponent }) => VipComponent),
    canDeactivate: [AutoGameDeactivateGuard],
    canActivate: [BanActivateGuard, IsAuthGuard, geoBlockGuard],
  },
  {
    path: 'game',
    loadChildren: () => import('./routes/games.routes').then(({ routes: gameRoutes }) => gameRoutes),
    providers: [importProvidersFrom(NgxsModule.forFeature([MotivatorState]))],
    canMatch: [surfaceAuthWallCanMatchGuard],
    canActivate: [BanActivateGuard, geoBlockGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    // Mirror surfaces with a static FAQ page (fastcs2) shadow the whole /faq subtree;
    // every legacy /faq/* deep link collapses to the page root.
    // No `seo` route data on purpose: the shared SEO.*.FAQ keys carry FastCase branding,
    // so the page keeps the surface default title/description and no bottom SEO block.
    // AutoGame* guards are deliberately omitted: auto-bet games are disabled on static-FAQ
    // surfaces — re-add them for parity before enabling the flag on a full-game surface.
    path: 'faq',
    canMatch: [staticFaqPageCanMatchGuard],
    canActivate: [BanActivateGuard],
    children: [
      {
        path: '',
        loadComponent: () => import('@app/modules/faq-static').then(({ FaqStaticComponent }) => FaqStaticComponent),
      },
      {
        path: '**',
        redirectTo: '',
      },
    ],
  },
  {
    path: 'faq',
    loadChildren: () => import('./routes/faq.routes').then(({ routes: faqRoutes }) => faqRoutes),
    data: { seo: 'FAQ' },
    canDeactivate: [AutoGameDeactivateGuard],
    canActivate: [BanActivateGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    path: 'store',
    loadComponent: () => import('@app/modules/p2p-new/lib/p2p-market.component').then(({ P2pMarketComponent }) => P2pMarketComponent),
    data: { seo: 'STORE' },
    canDeactivate: [AutoGameDeactivateGuard],
    canMatch: [surfaceAuthWallCanMatchGuard],
    canActivate: [BanActivateGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    path: APP_PATHS.betting,
    loadChildren: () =>
      import('@app/modules/betting').then(({ BettingPageComponent }) => [
        {
          path: '',
          component: BettingPageComponent,
          data: { seo: 'BETTING', seoType: [SeoType.TITLE, SeoType.DESCRIPTION, SeoType.H1, SeoType.MAIN], setIndex: true },
        },
        // Betby owns deep paths (/betting/e-sport/cs2) client-side; server returns the shell. Beta: deep paths not indexed.
        { path: '**', component: BettingPageComponent },
      ]),
    // The sportsbook is auth-only: a guest lands on /login (the auth modal) with this URL — query
    // included, so bt-path deep links survive — in `redirectToAfterLogin`; logging in returns them
    // here, dismissing the modal drops them to the home page (LoginComponent's flow). IsAuthGuard
    // passes on the server, so SSR/prerender still serve the shell and the guest is redirected after
    // hydration — same contract as the account pages. Auth runs LAST: the cheap static gates first,
    // and a guest on a betting-less/geo-blocked surface must not be bounced into the login flow.
    data: { redirectTo: '/login' },
    canDeactivate: [AutoGameDeactivateGuard],
    canActivate: [BanActivateGuard, bettingSurfaceGuard, geoBlockGuard, IsAuthGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    path: 'inventory',
    loadComponent: () => import('@app/modules/inventory').then(({ InventoryComponent }) => InventoryComponent),
    canDeactivate: [AutoGameDeactivateGuard],
    canActivate: [BanActivateGuard, geoBlockGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    path: 'items',
    loadChildren: () => import('@app/modules/item-details').then(({ routes: itemDetailsRoutes }) => itemDetailsRoutes),
    canDeactivate: [AutoGameDeactivateGuard],
    canActivate: [BanActivateGuard, geoBlockGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    path: 'account',
    loadChildren: () => import('@app/modules/account/account-fast.routes').then(({ routes: accountRoutes }) => accountRoutes),
    canDeactivate: [AutoGameDeactivateGuard],
    canActivate: [BanActivateGuard],
  },
  {
    path: 'events',
    data: { seo: 'EVENT' },
    loadChildren: () => import('@app/modules/events/lib/routes.constants').then(({ eventsRoutes }) => eventsRoutes),
    canDeactivate: [AutoGameDeactivateGuard],
    canMatch: [surfaceAuthWallCanMatchGuard],
    canActivate: [BanActivateGuard, geoBlockGuard],
    canActivateChild: [AutoGameActivateChildGuard],
  },
  {
    path: 'fail-payment',
    children: [
      {
        path: ':id',
        redirectTo: '/(modal:fail-payment/:id)',
      },
      {
        path: '**',
        redirectTo: '/',
      },
    ],
  },
  {
    path: 'success-payment',
    children: [
      {
        path: ':id',
        redirectTo: '/(modal:success-payment/:id)',
      },
      {
        path: '**',
        redirectTo: '/',
      },
    ],
  },

  {
    path: 'block',
    loadComponent: () => import('@app/ui/components/plug/plug.component').then((m) => m.PlugComponent),
  },
  {
    path: 'debug',
    loadComponent: () => import('@app/debug').then((m) => m.DebugMenuComponent),
    canActivate: [OnlyDevGuard],
  },
  {
    path: 'tickets',
    data: { seo: 'TICKETS', seoType: [SeoType.H1, SeoType.TITLE, SeoType.DESCRIPTION] },
    loadComponent: () => import('@app/modules/tickets').then((m) => m.TicketsFastComponent),
    providers: [importProvidersFrom(NgxsModule.forFeature([MotivatorState]))],
  },
  {
    path: 'game/tickets',
    redirectTo: 'tickets',
    pathMatch: 'full',
  },
  {
    path: 'anniversary',
    data: { seo: 'ANNIVERSARY' },
    loadChildren: () => import('ten-years').then(({ routes: tenYearsRoutes }) => tenYearsRoutes),
    canActivate: [casesOnlyGuard, geoBlockGuard],
  },
  {
    path: '11years',
    data: { seo: 'ANNIVERSARY_11' },
    loadChildren: () => import('anniversary-2').then(({ routes: anniversaryRoutes }) => anniversaryRoutes),
    canActivate: [casesOnlyGuard, geoBlockGuard],
  },
  {
    path: 'fifa',
    data: { seo: 'FIFA_EVENT', seoType: [SeoType.H1, SeoType.TITLE, SeoType.DESCRIPTION] },
    loadChildren: () => import('fifa').then(({ routes: fifaRoutes }) => fifaRoutes),
    canActivate: [fifaEventCanActivateGuard, geoBlockGuard],
  },
  {
    path: APP_PATHS.northPole,
    data: { seo: 'NORTH_POLE' },
    loadComponent: () => import('@app/modules/events/lib/north-pole').then(({ NorthPoleComponent }) => NorthPoleComponent),
    canActivate: [casesOnlyGuard],
  },
  {
    path: APP_PATHS.freeCoins,
    loadChildren: () => import('@app/modules/free-coins').then(({ routes: gameRoutes }) => gameRoutes),
    canActivate: [casesOnlyGuard],
  },
];
